/**
 * De onde vêm os números dos painéis da Equiplex.
 *
 * Texto único da página `/relatorios/origem-dos-dados` e do card "De onde vêm estes
 * dados" no topo de cada painel: mudou a regra de um número, muda aqui e as duas
 * telas acompanham.
 *
 * A linguagem é a de quem usa o painel, não a de quem mantém a réplica. O detalhe
 * técnico (tabelas, índices, pipeline, conferências completas) fica em
 * `docs/frente_dashboard_equiplex_linhagem.md`. Número conferido leva a data da
 * conferência, para o texto não envelhecer calado.
 */

export const ROTA_ORIGEM_DADOS = '/relatorios/origem-dos-dados';

export type ChavePainel = 'visao-geral' | 'maquinas' | 'sop' | 'vendas' | 'qualidade' | 'dre' | 'apontamentos';

export interface EtapaCaminho {
  id: string;
  icone: string;
  titulo: string;
  oQueAcontece: string;
  quando: string;
}

/** Datasul → cópia noturna → réplica → plataforma. */
export const CAMINHO: EtapaCaminho[] = [
  {
    id: 'datasul',
    icone: 'factory',
    titulo: 'Datasul',
    oQueAcontece: 'É onde a fábrica e a contabilidade lançam tudo: ordens de produção, fichas de CQ, notas fiscais, contábil.',
    quando: 'Na hora em que o pessoal lança.',
  },
  {
    id: 'copia',
    icone: 'sync_alt',
    titulo: 'Cópia noturna',
    oQueAcontece:
      'O Azure Data Factory copia as tabelas do Datasul, só lendo: nada é alterado lá. A cópia só entra se vier completa; senão fica a do dia anterior.',
    quando: '00:05 as notas fiscais · 02:00 a produção, as fichas de CQ e os cadastros · 03:30 o contábil, o orçamento e o saldo do CQ.',
  },
  {
    id: 'replica',
    icone: 'database',
    titulo: 'Réplica',
    oQueAcontece: 'Um espelho das tabelas do Datasul, para a plataforma ler sem pesar o sistema da fábrica.',
    quando: 'Guarda a última cópia até a próxima.',
  },
  {
    id: 'plataforma',
    icone: 'monitoring',
    titulo: 'Plataforma',
    oQueAcontece:
      'Lê a réplica e o Balanceamento de Linha, calcula os números de cada painel e guarda um retrato. A tela mostra esse retrato.',
    quando: '06:00 todo dia (ou "Atualizar agora", para admin).',
  },
];

/**
 * O que o horário das cargas significa para quem lê o painel. Regra de 16/10/2026 (pedido da
 * Karla): todas as abas fecham em 23:59 do dia anterior.
 */
export const LEITURA_DO_HORARIO =
  'Todas as abas vão até 23:59 do dia anterior: é o "Dados até" no topo de cada uma. A cópia do Datasul roda de madrugada e o painel é atualizado às 06:00; o que acontece hoje aparece amanhã. Se a cópia de alguma tabela atrasar, o "Dados até" volta para o último dia que ela tem inteiro. Na produção, um dia ainda cresce depois disso: a OP só é reportada no fim do processo, e os gráficos marcam até onde ela está "reportada".';

export interface NumeroPainel {
  nome: string;
  deOnde: string;
  comoCalculamos: string;
  /** Relatório do Datasul que mede a mesma coisa, ou a conferência feita. */
  noDatasul?: string;
}

export interface OrigemPainel {
  chave: ChavePainel;
  titulo: string;
  rota: string;
  /** Uma ou duas frases: é o texto do card no topo do painel. */
  resumo: string;
  numeros: NumeroPainel[];
  /** O dado nasce na plataforma, não no Datasul: o card não fala de cópia noturna. */
  daPlataforma?: boolean;
}

/** A régua única do produzido, decidida com PCP, produção e TI em 14/10/2026: o CP0511 sem reprocesso. */
const PRODUZIDO: NumeroPainel = {
  nome: 'Produzido',
  deOnde: 'Ordens de produção (OP) do Datasul: a quantidade reportada em cada uma.',
  comoCalculamos:
    'Soma da quantidade reportada nas OPs normais, pela data de início da OP (o dia em que a manipulação começa e o lote ganha vida). OP de reprocesso (as que começam com 100…) não entra: o lote reprocessado já contou na OP original.',
  noDatasul:
    'É o CP0511 sem as OPs de reprocesso. Conferido em 14/10/2026 com o CP0511 de ago/26: os 9 produtos iguais na unidade; tirando o reprocesso sai o número do painel.',
};

/** Decisão de 30/09/2026, vale para todo "programado" de OP. */
const SEM_OP_ENCERRADA =
  'OP encerrada sem produzir (Finalizada ou Terminada no Datasul, sem nada reportado) não conta: é OP cancelada ou reprogramada.';

/** Decisão de 01/10/2026, vale para todo desvio de mês em andamento (Visão geral, S&OP, Por máquina). */
const DIAS_FECHADOS =
  'No mês em andamento, só entram as OPs dos dias fechados (até dois dias antes do último dia com produção reportada): a OP é reportada de uma vez no fim do processo, então a dos dias mais recentes ainda está chegando. Elas aparecem na tela, mas só entram na conta quando o dia fecha. Programado e produzido saem das mesmas OPs.';

const FATURADO: NumeroPainel = {
  nome: 'Faturado',
  deOnde: 'Notas fiscais de saída no Datasul.',
  comoCalculamos:
    'Notas não canceladas de natureza que conta como venda, valor bruto, sem abater devolução. Venda para entrega futura conta uma vez, na nota de remessa (quando o produto sai).',
  noDatasul:
    'Bate, ao real, com a receita operacional da DRE de dez/25 a jun/26 (conferido em 29/09/2026). O keft417 conta a entrega futura na nota de faturamento, antes da remessa: nesses meses ele difere.',
};

export const ORIGEM_PAINEIS: OrigemPainel[] = [
  {
    chave: 'visao-geral',
    titulo: 'Visão geral',
    rota: '/paineis/equiplex/visao-geral',
    resumo:
      'Produzido = quantidade reportada nas ordens de produção, pela data de início da OP (o CP0511 sem reprocesso). Plano do mês = Balanceamento de Linha. Por máquina = programado × produzido das mesmas OPs.',
    numeros: [
      {
        ...PRODUZIDO,
        nome: 'Produzido no dia e no mês',
        comoCalculamos: `${PRODUZIDO.comoCalculamos} O mês em andamento cresce até as OPs dele serem reportadas.`,
      },
      {
        nome: 'Plano S&OP e meta do dia',
        deOnde: 'Balanceamento de Linha (o plano vivo do mês).',
        comoCalculamos:
          'Plano do mês de produção (dia 16 ao 15). Meta do dia = plano ÷ dias do mês. Com o filtro, cada dia vale o plano do mês de produção dele ÷ os dias desse mês: período que corta o mês leva o plano proporcional, e o mês comum soma os pedaços dos dois meses de produção que ele toca.',
      },
      {
        nome: 'Ritmo',
        deOnde: 'Os dois números acima.',
        comoCalculamos:
          'Produzido ÷ o que o plano previa, os dois até o último dia fechado (dois dias antes do último dia com produção reportada). Os dias mais recentes ainda estão sendo reportados e puxariam o ritmo para baixo.',
      },
      {
        nome: 'Programado × produzido das OPs, por máquina',
        deOnde: 'Ordens de produção.',
        comoCalculamos:
          `Programado = quantidade das OPs que começaram no mês. Produzido = o que foi reportado nessas OPs. Máquina = a primeira operação do roteiro da OP. ${DIAS_FECHADOS} ${SEM_OP_ENCERRADA}`,
        noDatasul:
          'Programado e máquina pela regra do relatório de produção por OP do Qlik (bi030). Relatório que soma cada operação no seu grupo de máquina (o KECP211, por exemplo) sai com outra quebra por máquina.',
      },
    ],
  },
  {
    chave: 'maquinas',
    titulo: 'Relatório Daily',
    rota: '/paineis/equiplex/maquinas',
    resumo:
      'As duas abas, a Daily e o MFR da reunião, geradas do Datasul: quantidade de cada OP no dia em que ela começa × o que foi reportado nela. Nenhuma planilha, nenhum rendimento teórico.',
    numeros: [
      {
        nome: 'Daily por linha e projeção do mês',
        deOnde:
          'Ordens de produção de frasco soprado e de produto acabado, sem reprocesso. A meta de cada máquina é digitada no painel pelo PCP (botão "Metas das máquinas").',
        comoCalculamos:
          'Cinco linhas: sopro consolidado, sopro de 100 ml e de 250 ml (pelo formato do frasco; as sopradoras 6 e 9 fazem 100 ml, a 7 e a 8 fazem 250 ml), grande volume (GF-1 e GF-2) e pequeno volume (Rommelag 1 e 2). Produzido = reportado nas OPs. Programação = a meta fixa de cada máquina por dia, somada nos dias do período (desde 16/09/2026: sopradoras 6 e 7 40.667, 8 44.064, 9 49.572; GF-1 83.016, GF-2 93.071; Rommelag 1 e 2 316.936 cada). Um dia está fechado quando todas as ordens da linha que começaram nele foram encerradas. Projeção = produzido dos dias fechados + programado dos dias com ordem aberta ou ainda por vir. OP encerrada sem produzir não conta.',
        noDatasul:
          'É a planilha "Projeção Produção" do Attila (PCP): o produzido bate OP a OP com a coluna "Realizado [líquido]" dela. A meta não existe no Datasul: é do PCP, e cada ajuste fica no histórico com o nome de quem fez (meta 0 marca um dia sem produção programada). Frasco comprado (sem OP) não entra.',
      },
      {
        nome: 'MFR',
        deOnde: 'Programado e produzido abaixo.',
        comoCalculamos:
          '1 − |programado − produzido| ÷ programado, por máquina e dia. Na semana (domingo a sábado) e no mês (16 a 15), soma o desvio de cada dia sem sinal: produzir a mais num dia não compensa o dia que faltou. Abaixo de 80% é alerta; abaixo de 50%, crítico.',
        noDatasul:
          'É a conta da planilha de MFR do Attila (conferida em 30/09/2026 na semana de 13 a 19/09). Desde 14/10/2026 o produzido é o reportado na OP, como no CP0511.',
      },
      {
        nome: 'Programado',
        deOnde: 'Ordens de produção (OP) de produto acabado e de frasco soprado, sem reprocesso.',
        comoCalculamos: `Quantidade da OP no dia em que ela começa (a data da planilha e do Qlik). Máquina = a primeira operação do roteiro. ${SEM_OP_ENCERRADA}`,
        noDatasul:
          'Na planilha o programado do sopro é uma meta de capacidade digitada (39.870 a 48.600 por dia). Aqui é a OP: o número muda de patamar.',
      },
      {
        ...PRODUZIDO,
        nome: 'Produzido das OPs',
        comoCalculamos:
          'Quantidade reportada na OP, no dia em que ela começou — assim a OP carrega os dois lados. A planilha, quando falta o realizado, usa o rendimento teórico do lote; aqui fica zero até a OP ser reportada.',
      },
      {
        nome: 'Dia aberto',
        deOnde: 'Último dia de início com produção reportada na cópia.',
        comoCalculamos:
          'A OP é reportada de uma vez no fim do processo, então as dos dois últimos dias ainda estão chegando. O dia aparece, mas só entra na semana e no mês quando fecha.',
      },
      {
        nome: 'OPs sem apontamento',
        deOnde: 'Situação da OP e quantidade reportada no Datasul.',
        comoCalculamos:
          '"Não iniciada" = o dia já fechou e a OP nem começou (continua no programado). "Iniciada sem apontamento" = a OP está iniciada no Datasul, o dia já fechou e nada foi reportado.',
      },
    ],
  },
  {
    chave: 'sop',
    titulo: 'S&OP e desvios',
    rota: '/paineis/equiplex/sop',
    resumo:
      'Programado = ordens de produção. Produzido = o que foi reportado nessas OPs (o CP0511 sem reprocesso). Faturado = notas fiscais de venda. Tudo por mês de produção (16 a 15).',
    numeros: [
      {
        nome: 'Programado',
        deOnde: 'Ordens de produção (OP) no Datasul.',
        comoCalculamos:
          `Quantidade das OPs de produto acabado (sem reprocesso), pela data de início da OP. ${SEM_OP_ENCERRADA}`,
      },
      {
        ...PRODUZIDO,
        nome: 'Produzido das OPs do mês',
        comoCalculamos:
          'Quantidade reportada nas OPs normais que começaram no mês, pela data de início da OP: a mesma OP dos dois lados do desvio. É o mesmo produzido da Visão geral e do Por máquina.',
      },
      {
        nome: 'Desvio',
        deOnde: 'Programado e produzido.',
        comoCalculamos: `(produzido − programado) ÷ programado. Mais de 20% para cima ou para baixo é alerta; mais de 50%, crítico. ${DIAS_FECHADOS}`,
      },
      {
        nome: 'Produzido por linha',
        deOnde: 'As mesmas OPs, agrupadas pela linha do item; o Green Pack, das OPs de frasco soprado.',
        comoCalculamos:
          'Grande volume = famílias 111 (SPGV-SF) e 112 (Clister); pequeno volume = 121 (SPPV-P BFS) e 124 (SPPV-P BFS exportação). Nas famílias 150 e 151 (lotes piloto de grande e de pequeno volume) a linha é do item, pela máquina em que ele roda: GF-1/GF-2 grande volume, Rommelag pequeno volume. Fábrica = produto acabado, bate com o resumo por mês. % da fábrica = parte do produzido do mês que a linha fez. Green Pack = o sopro (OPs de frasco soprado, mesma regra), fora da fábrica: o frasco vira produto acabado e contaria duas vezes.',
      },
      { ...FATURADO, nome: 'Faturamento' },
    ],
  },
  {
    chave: 'vendas',
    titulo: 'Forecast e vendas',
    rota: '/paineis/equiplex/vendas',
    resumo:
      'Forecast = demanda do comercial importada no Balanceamento de Linha. Faturado = notas fiscais de venda de produto acabado. UF = estado de destino da nota.',
    numeros: [
      {
        nome: 'Forecast',
        deOnde: 'Balanceamento de Linha (o forecast do comercial que o planejamento importa lá).',
        comoCalculamos:
          'Demanda de cada SKU no mês de produção. R$ = quantidade × preço unitário do cadastro do Balanceamento. Com o filtro, cada dia vale o forecast do mês de produção dele ÷ os dias desse mês.',
        noDatasul: 'O Datasul não tem forecast em uso: as tabelas de previsão pararam em 2018.',
      },
      { ...FATURADO, comoCalculamos: `${FATURADO.comoCalculamos} Só produto acabado, por mês de produção.` },
      {
        nome: 'UF',
        deOnde: 'Estado de destino na nota fiscal.',
        comoCalculamos:
          'Ranking do faturado real por estado; EX = exportação. O forecast não tem UF. Sem período escolhido no filtro, últimos 12 meses; com período, o do período.',
      },
    ],
  },
  {
    chave: 'dre',
    titulo: 'DRE e desvios P&L',
    rota: '/paineis/equiplex/dre',
    resumo:
      'Saldo contábil do Datasul, na classificação da DRE do próprio ERP (programa bi060). Orçado = orçamento contábil do Datasul (BGC). Nada vem da planilha da Controladoria.',
    numeros: [
      {
        nome: 'Receita, custos e despesas',
        deOnde: 'Saldo contábil do Datasul (EMS5), empresa Equiplex, plano de contas IFRS.',
        comoCalculamos:
          'Crédito menos débito de cada conta no mês, somado pela linha da DRE. Mês civil, o da contabilidade (não o de 16 a 15 da produção).',
        noDatasul: 'A receita bruta é igual, ao real, à soma das notas fiscais de saída em todos os meses de out/25 a jul/26.',
      },
      {
        nome: 'Linhas da DRE',
        deOnde: 'Classificação de cada conta na DRE do Datasul (programa bi060, a mesma que o Qlik usava).',
        comoCalculamos:
          'Conta que não está na classificação entra em "Contas sem classificação no Datasul", antes do resultado antes do IR: o lucro líquido é sempre a soma de todas as contas de resultado.',
        noDatasul:
          'A planilha da Controladoria classifica diferente: lá o incentivo fiscal (CPC 07) abate as deduções; no Datasul ele fica em Subvenções governamentais, abaixo do lucro bruto. Por isso a receita líquida e o lucro bruto da planilha ficam cerca de R$ 2 mi por mês acima. O lucro líquido é o mesmo.',
      },
      {
        nome: 'Composição do custo',
        deOnde: 'Grupos do plano de contas do Datasul.',
        comoCalculamos:
          'Custo de produção do mês por natureza (materiais, mão de obra direta e indireta, ocupação e serviços, outros), mais a absorção pelo estoque e o custo das vendas: a soma é o custo dos produtos vendidos, ao real.',
      },
      {
        nome: 'EBITDA',
        deOnde: 'Resultado operacional e as contas de depreciação e amortização.',
        comoCalculamos:
          'Resultado operacional mais a depreciação e a amortização das contas de custo e despesa operacional, sem as contas de absorção e rateio. O Datasul não tem essa linha: a regra é da plataforma.',
      },
      {
        nome: 'Orçado',
        deOnde: 'Orçamento contábil do Datasul (BGC), unidade Equiplex, versão aprovada do ano.',
        comoCalculamos:
          'Só despesas e custo de produção por natureza: receita, deduções e custo das vendas não são orçados. Atenção acima de 10% do orçado, crítico acima de 25%; desvio menor que R$ 50 mil não vira alerta.',
      },
      {
        nome: 'Posição do contábil',
        deOnde: 'Hora da cópia do contábil e receita das notas fiscais do mês.',
        comoCalculamos:
          'Todo mês com lançamento aparece. Mês com receita contábil abaixo de 98% da receita das notas fica marcado como parcial: a contabilidade ainda está lançando.',
        noDatasul:
          'O Datasul não fecha período: a contabilidade lança em mês passado a qualquer hora, e o número muda na cópia seguinte. Entre 16 e 22/09/2026 entraram R$ 2,8 mi de juros de empréstimo em jan a jun/26.',
      },
    ],
  },
  {
    chave: 'qualidade',
    titulo: 'Liberação da Qualidade',
    rota: '/paineis/equiplex/qualidade',
    resumo:
      'A fila "aguardando CQ" sai do saldo do depósito do Controle da Qualidade, como no CE0811. Lotes e prazos saem das fichas de CQ do Datasul: quando o lote entrou no CQ e quando foi inspecionado e liberado.',
    numeros: [
      {
        nome: 'Aguardando CQ (a fila)',
        deOnde: 'Saldo de estoque nos depósitos do Controle da Qualidade: CQ e CQV (validação).',
        comoCalculamos:
          'Lote com saldo no depósito do CQ, contado uma vez, com a quantidade do saldo. Ficha pendente sem saldo no depósito (lote reprocessado, OP estornada) não entra: aparece na lista "Fichas abertas sem saldo no CQ", para a Qualidade encerrar no Datasul. Devolução (depósito DV) fica de fora: não é produção.',
        noDatasul:
          'Mesmo saldo do CE0811 (decisão de 09/10/2026 com Marcel, Johnattan e Braz). Em 15/10/2026: 304 lotes com saldo no CQ, 300 com ficha pendente; 95 fichas pendentes sem saldo, quase todas de antes de 2026 (a mais antiga, de 2020).',
      },
      {
        nome: 'Status do lote',
        deOnde: 'Fichas de CQ de produção.',
        comoCalculamos:
          'Pendente = ainda não inspecionada. Inspecionada = já passou pela inspeção, liberada ou não. Ficha cancelada fica de fora. A cópia do saldo chega às 03:30 e a da ficha às 02:00, as duas antes do retrato das 06:00.',
        noDatasul: 'Mesmas situações da consulta de liberação do CQ (kecq200).',
      },
      {
        nome: 'Entrou no CQ',
        deOnde: 'Fichas de CQ de produção.',
        comoCalculamos:
          'Lotes e unidades pela data de entrada no CQ (quando o lote entra no estoque e ganha a ficha). É outra data: o produzido das outras abas é pela data de início da OP, então no mesmo período os dois não batem.',
      },
      {
        nome: 'Prazo de liberação',
        deOnde: 'Fichas de CQ de produção.',
        comoCalculamos: 'Dias corridos entre a entrada no CQ e a inspeção. Lote ainda no CQ conta até 23:59 do dia anterior.',
      },
      {
        nome: 'Previsão e desvio de liberação',
        deOnde: 'Fichas de CQ de produção e o prazo da Qualidade.',
        comoCalculamos:
          'Previsão = data de entrada no CQ + 14 dias, o prazo da Qualidade para qualquer produto acabado (16/10/2026). Desvio = se o lote passou ou não da previsão: quem já saiu do CQ, pela data da inspeção; quem ainda está, se continuava no CQ às 23:59 do dia da previsão. Frasco ainda sem prazo: fica sem previsão.',
        noDatasul:
          'O Datasul não guarda a previsão: o prazo de CQ do cadastro do item está zerado no produto acabado, e a data de saída do exame na ficha é a mesma da entrada. Prazo real de 16/06 a 15/10/2026: 14,7 dias no SF 100 ml e 14,9 no SF 250 ml.',
      },
      {
        nome: 'Máquina',
        deOnde: 'Ordem de produção do lote.',
        comoCalculamos: 'A primeira operação do roteiro da OP, a mesma regra da Visão geral.',
      },
      {
        nome: 'Lotes reprocessados',
        deOnde: 'Ordens de produção de reprocesso (as OPs 100…, tipo reprocesso no Datasul).',
        comoCalculamos:
          'Lotes com OP de reprocesso reportada, pela data de início dessa OP, como o produzido. A taxa é sobre os lotes das OPs normais do mesmo mês. Só produto acabado; não abre por máquina.',
        noDatasul: 'Conferido em 15/10/2026: desde jul/26, as 74 OPs de reprocesso têm a OP original no mesmo lote. Set/26 (16/08 a 15/09): 27 de 382 lotes, 7,1%.',
      },
    ],
  },
  {
    chave: 'apontamentos',
    titulo: 'Apontamentos da reunião',
    rota: '/paineis/equiplex/apontamentos',
    daPlataforma: true,
    resumo:
      'Registrados na plataforma por quem participa da reunião. Não vêm do Datasul e não mudam nenhum número dos painéis: explicam o porquê de um desvio.',
    numeros: [
      {
        nome: 'Apontamento',
        deOnde: 'Digitado na plataforma por quem tem acesso aos painéis.',
        comoCalculamos:
          'Nada é calculado: data, máquina, SKU, turno, motivo, quantidade afetada e observação ficam como foram digitados. Máquinas e SKUs são os mesmos da Visão geral.',
      },
      {
        nome: 'Quem registrou e o histórico',
        deOnde: 'Usuário logado na plataforma.',
        comoCalculamos:
          'Cada linha mostra quem registrou. Só essa pessoa (ou um admin) edita ou apaga; toda mudança fica no histórico, com quem fez e como estava. Apagar tira da tela, não do histórico.',
      },
      {
        nome: 'Mês e relatório da reunião',
        deOnde: 'Data do apontamento.',
        comoCalculamos:
          'O apontamento entra no mês de produção (16 a 15) da sua data, igual à Visão geral. O botão "Relatório da reunião (PDF)" da Visão geral leva o mês escolhido e os apontamentos dele.',
      },
    ],
  },
];

export interface EtapaLote {
  titulo: string;
  detalhe: string;
  /** Quem mede a produção nesta etapa (texto do selo). */
  marca?: string;
}

/** O caminho de um lote: em que data e com que quantidade a produção conta, e onde começa a Qualidade. */
export const JORNADA_DO_LOTE: EtapaLote[] = [
  {
    titulo: 'OP começa',
    detalhe: 'A manipulação começa e o lote ganha vida. É a data de início da OP, a de todos os relatórios do Datasul.',
    marca: 'A produção do lote conta neste dia',
  },
  { titulo: 'Linha produz', detalhe: 'Sopro, envase, esterilização e revisão; as caixas vão para os pallets no WMS.' },
  {
    titulo: 'OP reportada',
    detalhe: 'A produção é reportada na OP (FTF), de uma vez, no fim do processo: em geral dois a três dias depois do início.',
    marca: 'O CP0511 e os painéis contam esta quantidade',
  },
  { titulo: 'Entra no CQ', detalhe: 'O lote entra no estoque e ganha a ficha de CQ.', marca: 'A aba de Qualidade começa aqui' },
  { titulo: 'CQ libera', detalhe: 'A Qualidade inspeciona e libera o lote.' },
];

/** Onde o painel e o CP0511 ainda podem mostrar números diferentes. */
export const DIFERENCAS_PRODUZIDO: string[] = [
  'Reprocesso: o CP0511 soma também as OPs de reprocesso (as que começam com 100…), e o lote reprocessado conta duas vezes, na OP original e na de reprocesso. O painel conta uma vez, na OP original. Em ago/26, Água para Injeção 10ML BFS: 6.733.000 no CP0511, 6.629.600 no painel.',
  'Mês em andamento: a OP só é reportada no fim do processo, então o número do mês cresce até todas as OPs dele fecharem. Um CP0511 tirado antes disso mostra menos que o painel dias depois.',
  'Hora da conta: o CP0511 é tirado na hora; o painel é o retrato das 06:00, com a cópia do Datasul da madrugada. OP reportada depois das 02:00 aparece no dia seguinte.',
  'Mês: o painel usa o mês de produção (16 a 15). Para comparar com um CP0511 do dia 1 ao último dia, escolha "mês comum" no filtro.',
  'Máquina: os painéis põem a OP inteira na máquina da primeira operação do roteiro (a regra do relatório de produção do Qlik); relatório que soma cada operação no seu grupo de máquina sai com outra quebra.',
];

export interface ItemFiltro {
  titulo: string;
  texto: string;
}

/** O filtro dos painéis (período, tipo de mês, produto): o que ele faz em cada aba. */
export const FILTRO_PAINEIS: ItemFiltro[] = [
  {
    titulo: 'Período',
    texto:
      'Atalhos (mês atual, mês anterior, últimos 3, 6 ou 12 meses, últimos 7 ou 30 dias) ou datas escolhidas no calendário. Os atalhos contam a partir de hoje. O painel guarda 13 meses de dados; período mais antigo começa no primeiro dia guardado, e a tela avisa.',
  },
  {
    titulo: 'Sem período escolhido',
    texto:
      'S&OP, Qualidade, Visão geral e Relatório Daily abrem no mês atual (desde 27/10/2026); Forecast e vendas, nos últimos 6 meses; DRE, nos últimos 12. Os gráficos e tabelas mês a mês (volume, faturamento, resumo por mês, produzido por linha, prazo por mês, matriz por máquina) mostram pelo menos os 6 meses até o fim do período, para comparar. Nos primeiros dias do mês de produção, a barra avisa que o mês ainda tem poucos dias e leva ao mês anterior num clique.',
  },
  {
    titulo: 'Mês de produção ou mês comum',
    texto:
      'Mês de produção vai do dia 16 ao dia 15, como a fábrica e o Balanceamento; mês comum, do dia 1 ao último dia. Vale para os atalhos de mês e para os gráficos mês a mês. Mês cortado pelo período aparece com os dias de fato.',
  },
  {
    titulo: 'Produtos',
    texto:
      'Um ou vários, buscando pelo número (com ou sem ponto) ou pelo nome. O filtro vale para plano, produção, OPs, lotes, faturado e forecast dos produtos escolhidos.',
  },
  {
    titulo: 'Meta e forecast num período que corta o mês',
    texto:
      'Cada dia vale o plano (ou o forecast) do mês de produção dele dividido pelos dias desse mês. O mês inteiro dá o plano inteiro; um pedaço dá a parte dele; o mês comum soma os pedaços dos dois meses de produção que ele toca.',
  },
  {
    titulo: 'O que muda em cada aba',
    texto:
      'Qualidade: a fila são os lotes ainda no CQ que entraram até o fim do período. S&OP: o aviso de faturamento abaixo do normal some com filtro de produto. Forecast e vendas: o ranking de UF é do período (sem período, dos últimos 12 meses). Apontamentos: com filtro de produto, o apontamento sem SKU continua, porque vale para todos. DRE: é contábil, vai pelos meses comuns inteiros e não abre por produto.',
  },
  {
    titulo: 'Como é feito',
    texto:
      'O filtro fica no endereço da página e passa de uma aba para outra pelo menu. A plataforma refaz a aba a partir do retrato das 06:00, com as mesmas contas de sempre — o filtro não consulta o Datasul e não muda número nenhum, só escolhe o pedaço.',
  },
];

export interface Conferencia {
  titulo: string;
  resultado: string;
  data: string;
}

export const CONFERENCIAS: Conferencia[] = [
  {
    titulo: 'Datasul × cópia',
    resultado:
      'Em todas as tabelas dos painéis, a cópia da noite trouxe o mesmo número de linhas que o Datasul tinha, inclusive os 3,7 milhões de caixas dos pallets.',
    data: '29/09/2026',
  },
  {
    titulo: 'Produzido × CP0511',
    resultado:
      'O CP0511 de ago/26 é a soma do reportado em todas as OPs pela data de início: os 9 produtos iguais na unidade. O painel tira as OPs de reprocesso.',
    data: '14/10/2026',
  },
  {
    titulo: 'Faturado × DRE',
    resultado: 'Igual, ao real, à receita operacional da DRE em todos os meses de dez/25 a jun/26.',
    data: '29/09/2026',
  },
  {
    titulo: 'Contábil × notas fiscais',
    resultado:
      'A receita bruta do contábil é igual, ao real, à soma das notas fiscais de saída em todos os meses de out/25 a jul/26. Em ago/26 o contábil tinha 90%: o mês ainda estava sendo lançado.',
    data: '30/09/2026',
  },
  {
    titulo: 'Custo aberto × custo da DRE',
    resultado: 'A composição pelo plano de contas soma, ao real, o custo dos produtos vendidos em todos os meses de 2026.',
    data: '30/09/2026',
  },
];

export interface TermoGlossario {
  termo: string;
  significado: string;
}

export const GLOSSARIO: TermoGlossario[] = [
  { termo: 'Ficha de CQ', significado: 'Registro criado quando o lote entra no controle de qualidade. Guarda a quantidade produzida.' },
  {
    termo: 'Depósito do CQ',
    significado:
      'Onde o lote fica em quarentena até a Qualidade liberar: CQ (produção) e CQV (validação). A fila do painel é o saldo desses depósitos, como no CE0811.',
  },
  { termo: 'OP', significado: 'Ordem de produção: o pedido interno para produzir um item, com a quantidade programada e o roteiro de máquinas.' },
  {
    termo: 'CP0511',
    significado: 'Relatório de produção do Datasul: soma o reportado nas OPs pela data de início. Os painéis fazem a mesma conta, sem o reprocesso.',
  },
  {
    termo: 'OP de reprocesso',
    significado:
      'OP aberta para refazer um lote já produzido. Começa com 100… (as normais começam com 200…). Não conta como produção: o lote já contou na OP original.',
  },
  {
    termo: 'Dia fechado',
    significado:
      'Dia cujas OPs já foram reportadas: até dois dias antes do último dia com produção reportada. No mês em andamento, ritmo, desvio e MFR só olham os dias fechados.',
  },
  { termo: 'Mês de produção', significado: 'Do dia 16 ao dia 15 do mês seguinte: o calendário da fábrica e do Balanceamento.' },
  {
    termo: 'Mês comum',
    significado:
      'Do dia 1 ao último dia do mês. Pode ser escolhido no filtro dos painéis, no lugar do mês de produção; a DRE é sempre por mês comum.',
  },
  {
    termo: 'Filtro do painel',
    significado:
      'Período (atalho ou datas no calendário), tipo de mês e produtos. Fica no endereço da página e vale para as abas que já o aceitam. O painel é refeito a partir do retrato do dia, sem consultar o Datasul.',
  },
  { termo: 'Réplica', significado: 'Cópia das tabelas do Datasul, só para leitura.' },
  { termo: 'Retrato', significado: 'O resultado dos cálculos guardado às 06:00. Abrir o painel não consulta o Datasul.' },
  { termo: 'DRE', significado: 'Demonstração do resultado: receita, custos e despesas do mês, até o lucro líquido.' },
  {
    termo: 'EBITDA',
    significado: 'Resultado operacional antes da depreciação e da amortização: o que a operação gera de caixa, antes de juros e impostos.',
  },
  { termo: 'Orçado (BGC)', significado: 'Orçamento contábil cadastrado no Datasul pela controladoria, por conta e por mês.' },
  {
    termo: 'Venda para entrega futura',
    significado: 'Venda faturada antes e entregue depois. Gera duas notas, a de faturamento e a de remessa; conta uma vez, na remessa.',
  },
];

export function origemDoPainel(chave: ChavePainel): OrigemPainel {
  const origem = ORIGEM_PAINEIS.find((p) => p.chave === chave);
  if (!origem) throw new Error(`Painel sem origem cadastrada: ${chave}`);
  return origem;
}

/** Link do card para a seção do painel na página. */
export function linkOrigem(chave: ChavePainel): string {
  return `${ROTA_ORIGEM_DADOS}#${chave}`;
}
